/**
 * WebSocket client for WS-Print (/ws/print)
 * Builds URL from Vite env (`VITE_API_URL`) and handles identification headers.
 */

import printerService from "./printerService";

function buildWsPrintUrl() {
  try {
    const apiBase = import.meta.env?.VITE_API_URL;
    if (apiBase) {
      const u = new URL(apiBase);
      const scheme = u.protocol === 'https:' ? 'wss' : 'ws';
      // Strip trailing '/api' or '/v1'
      const basePath = u.pathname.replace(/\/?api(\/v1)?\/?$/, '').replace(/\/$/, '');
      const url = `${scheme}://${u.hostname}${u.port ? `:${u.port}` : ''}${basePath}/ws/print`;

      return url;
    }
  } catch { void 0 }

  // Fallback to current origin
  const loc = window.location;
  const scheme = loc.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${scheme}://${loc.host}/ws/print`;

  return url;
}

class WsPrintClient {
  constructor() {
    this.websocket = null;
    this._connecting = false;
    this._reconnectDelayMs = 3000;
    this.ident = null;
    this.uuid = null;
    this.pairedWith = null;
    this.printerName = null;
    this._pending = [];
    this._debug = true; // habilita logs por padrío
    ////this._log = (level, ...args) => { try { (console[level] || console.log).call(console, '[WS-PRINT]', ...args); } catch {} };
    this.callbacks = {
      onOpen: null,
      onClose: null,
      onError: null,
      onMessage: null,
      onStatus: null, // generic status updates
      onPrinter: null, // printer selected/updated
      onUnpaired: null, // despareado
      onPrintResult: null, // retorno_impressao_ok|error
      onPrinters: null, // lista de impressoras
    };
  }

  setCallbacks(cb) {
    this.callbacks = { ...this.callbacks, ...cb };
  }

  get isConnected() {
    return !!this.websocket && this.websocket.readyState === WebSocket.OPEN;
  }

  get isConnecting() {
    return !!this.websocket && this.websocket.readyState === WebSocket.CONNECTING;
  }

  async reParear(ident) {
    if (this.websocket && (this.isConnected || this.isConnecting)) {
      try {
        if (ident?.pairWithUuid) {
          // Servidor wsServer.js usa 'parear' como destino; incluímos 'uuid' igual ao input
          alert('parear com: ' + ident.pairWithUuid)
          let pairMsg = { parear: ident.pairWithUuid, uuid: ident.id };
          ident.pairWithUuid = null
          setPairedWith(null)
          wsPrintClient.pairedWith = null;
          if (this.callbacks.onStatus) this.callbacks.onStatus({ pairedWith: null, connected: true });

          this.websocket.send(JSON.stringify(pairMsg));
        }
      } catch { void 0 }
      alert('aguardar 5 segundos');

      await setTimeout(() => {
        if (!wsPrintClient.pairedWith) {
          try {
            setPrinterName(null);
            setPrinterDevice(null);
            setPrinterEmulator(null);
            setPairedWith(null);
            setUuid('');
            setIpModalVisible(false);
            message.warning('Pareamento não realizado. Verifique o ID e tente novamente.');
            try { wsPrintClient.websocket?.close(); } catch { }

          } catch (e) { console.warn(e); }
        }
        alert('5 segundos OK');

        resolve(true);
      }, 5000);
    } else {
      resolve(true);
    }

  }//

  async initializeWebsocket(ident) {
    this.ident = ident; // { empresa, id: clientUUID, server, computer, nome, usuario, lote, emparelhar, versao, pairWithUuid }
    const url = buildWsPrintUrl();
    //if (this._debug) //this._log('debug', 'Inicializando conexío', { url, ident });

    return new Promise((resolve, reject) => {
      try {
        if (this.websocket && (this.isConnected || this.isConnecting)) {
          alert('Já conectado ou conectando;  dispara parear');
          this.reParear(ident)
            .then(() => {
              return;
            })
            .catch(() => {
              return;
            });
          return;
        }

        this._connecting = true;
        this.websocket = new WebSocket(url);
        if (this._debug) //this._log('debug', 'WebSocket criado, aguardando onopen');

          this.websocket.onopen = () => {
            if (this._debug) //this._log('info', 'Conexío aberta', { url });
              try {
                // Send identification headers as first message
                const headers = {
                  // Dados do usuário logado conforme especificação
                  empresa: ident?.branches?.sigla ?? ident?.empresa ?? '',
                  id: ident?.id,
                  server: false,
                  computer: ident?.computer,
                  nome: ident?.codigo ?? ident?.nome ?? '',
                  usuario: ident?.name ?? ident?.usuario ?? '',
                  lote: '',
                  emparelhar: '',
                  versao: 'on board',
                };
                const headersMsg = JSON.stringify({ headers });
                this.websocket.send(headersMsg);
                if (this._debug) //this._log('debug', 'Headers enviados', headers);
                  if (ident?.id) {
                    const uuidMsg = JSON.stringify({ uuid: ident.id });
                    this.websocket.send(uuidMsg);
                    //if (this._debug) //this._log('debug', 'UUID enviado', { uuid: ident.id });
                  }
                if (ident?.pairWithUuid) {
                  // Envia pareamento com destino igual ao valor do input (compatível com wsServer.js)
                  const pairMsg = { parear: ident.pairWithUuid, uuid: ident.pairWithUuid };
                  this.websocket.send(JSON.stringify(pairMsg));
                  //if (this._debug) //this._log('info', 'Pareamento enviado (onopen)', pairMsg);
                }
              } catch (e) {
                console.warn('Falha ao enviar headers/uuid:', e);
              }

            this._connecting = false;
            if (this.callbacks.onOpen) this.callbacks.onOpen({ url });
            //if (this.callbacks.onStatus) this.callbacks.onStatus({ connected: true });
            if (!ident?.pairWithUuid) {
              if (this.callbacks.onStatus) this.callbacks.onStatus({ connected: true });
            }
            try {
              if (this._pending && this._pending.length) {
                if (this._debug) //this._log('debug', 'Enviando fila pendente', this._pending.length);
                  this._pending.forEach((p) => { try { this.websocket.send(p); } catch { void 0 } });
                this._pending = [];
              }
            } catch { void 0 }
            resolve(true);
          };

        this.websocket.onerror = (error) => {
          this._connecting = false;
          if (this._debug) //this._log('error', 'Erro na conexío', error);
            if (this.callbacks.onError) this.callbacks.onError(error);
          this.isConnected = false;
          this.printerName = null;
          reject(error);
        };

        this.websocket.onclose = (event) => {
          this._connecting = false;
          if (this._debug) //this._log('warn', 'Conexío fechada', { code: event.code, reason: event.reason });
            if (this.callbacks.onClose) this.callbacks.onClose(event);
          if (this.callbacks.onStatus) this.callbacks.onStatus({ connected: false });
          this.pairedWith = null;
          // Auto-reconnect
          setTimeout(() => {
            if (!this.isConnected) {
              this.initializeWebsocket(this.ident).catch(() => { });
            }
          }, this._reconnectDelayMs);
        };

        this.websocket.onmessage = (messageEvent) => {
          const msg = messageEvent.data;

          if (this._debug) //this._log('debug', 'Mensagem recebida (raw)', msg);
            // Respond to string-based ping if used
            if (msg === 'ping') {
              try { this.websocket.send('pong'); } catch { void 0 }
              if (this._debug) //this._log('debug', 'Respondido pong');
                return;
            }

          // Try parse JSON
          let data = null;
          if (typeof msg === 'string' && msg.includes('{') && msg.includes('}')) {
            try { data = JSON.parse(msg); } catch { void 0 }
          }

          if (data && typeof data === 'object') {
            const key = Object.keys(data)[0];
            if (this._debug) //this._log('debug', 'Mensagem JSON', { key, data });
              switch (key) {
                case 'uuid':
                  // remote echoes back uuid
                  this.uuid = data.uuid;
                  if (this._debug) //this._log('info', 'UUID ecoado pelo servidor', data.uuid);
                    if (this.callbacks.onStatus) this.callbacks.onStatus({ uuid: data.uuid });
                  break;
                case 'parear':
                case 'pareado':
                  if (this._debug) //this._log('info', `Evento ${key} recebido`, data[key]);
                    this.pairedWith = data[key];
                  //if (this.callbacks.onStatus) this.callbacks.onStatus({ pairedWith: data[key] });
                  if (this.callbacks.onStatus) this.callbacks.onStatus({ pairedWith: data[key], connected: true });
                  //this.callbacks.onPrinter('');
                  break;

                case 'no_paredo':
                case 'no_pareado':
                  alert('pareamento recusado: ' + data[key]);
                  if (this._debug) //this._log('warn', `Evento ${key} recebido (pareamento recusado)`, data[key]);
                    if (this.callbacks.onStatus) this.callbacks.onStatus({ pairedWith: null });
                  break;
                case 'impressora':
                  if (this._debug) //this._log('info', 'Impressora atualizada', data.nome || data.impressora);
                    if (this.callbacks.onPrinter) this.callbacks.onPrinter(data.nome || data.impressora);
                  break;

                case 'impressoras':
                  if (Array.isArray(data.impressoras)) {
                    if (this._debug) //this._log('info', 'Impressoras recebidas', data.impressoras.length);
                      if (this.callbacks.onPrinters) this.callbacks.onPrinters(data.impressoras);
                  }
                  break;


                case 'sel_impressora':
                  if (data?.nome != null && String(data.nome).trim() !== '') {
                    if (this._debug) //this._log('info', 'Impressora selecionada', data.nome);
                      printerService.connectionType = 'socket';
                    printerService.printerName = data.nome;
                    this.printerName = data.nome;
                    this.connectionType = 'socket';
                    if (this.callbacks.onPrinter) this.callbacks.onPrinter(data.nome);
                    printerService.connectionType = 'socket';
                    printerService.printerName = data.nome;
                    this.printerName = data.nome;
                    this.connectionType = 'socket';
                    try {
                      const a = new Audio('/sound/printer_ok.mp3');
                      a.play().catch(() => { });
                    } catch { void 0 }


                  }
                  break;
                case 'impressora_erro':
                  if (this._debug) //this._log('error', 'Erro de impressora recebido');
                    if (this.callbacks.onError) this.callbacks.onError(new Error('impressora_erro'));
                  break;
                case 'desparear':
                case 'despareado':
                  if (this._debug) //this._log('warn', 'Despareamento recebido', data.mensagem || data.desparear || data.despareado);
                    if (this.callbacks.onUnpaired) this.callbacks.onUnpaired(data.mensagem || data.desparear || data.despareado);
                  break;
                case 'retorno_impressao_ok':
                  if (this._debug) this._log('info', 'Retorno impressão OK');
                  if (this.callbacks.onPrintResult) this.callbacks.onPrintResult({ ok: true });
                  break;
                case 'retorno_impressao_error':
                  if (this._debug) //this._log('error', 'Retorno impressão ERROR');
                    if (this.callbacks.onPrintResult) this.callbacks.onPrintResult({ ok: false });
                  break;
                default:
                  // passthrough to consumer
                  if (this._debug) //this._log('debug', 'Mensagem não categorizada', data);
                    if (this.callbacks.onMessage) this.callbacks.onMessage(data);
                  break;
              }
            return;
          }

          // Non-JSON message passthrough
          if (this._debug) //this._log('debug', 'Mensagem não-JSON', msg);
            if (this.callbacks.onMessage) this.callbacks.onMessage(msg);
        };
      } catch (error) {
        this._connecting = false;
        if (this._debug) //this._log('error', 'Falha ao inicializar WebSocket', error);
          if (this.callbacks.onError) this.callbacks.onError(error);
        reject(error);
      }
    });
  }

  emit(objOrString) {
    try {
      const payload = typeof objOrString === 'string' ? objOrString : JSON.stringify(objOrString);
      if (!this.isConnected) {
        if (this._debug) //this._log('warn', 'WebSocket não está aberto, enfileirando', payload);
          this._pending.push(payload);
        return;
      }

      this.websocket.send(payload);
      console.log('>> enviado: ' + payload)
    } catch (e) {
      if (this._debug) //this._log('error', 'Falha ao emitir mensagem', e);
        if (this.callbacks.onError) this.callbacks.onError(e);
    }
  }

  pairWith(destUuid) {
    try {
      if (!this.isConnected) throw new Error('WebSocket não está aberto');
      const originId = this.ident?.id;
      if (!originId) throw new Error('UUID do cliente não definido');
      const msg = { parear: originId, uuid: destUuid };
      if (this._debug) //this._log('info', 'Pareamento solicitado via API', msg);
        this.websocket.send(JSON.stringify(msg));
    } catch (e) {
      if (this._debug) //this._log('error', 'Falha ao parear', e);
        if (this.callbacks.onError) this.callbacks.onError(e);
    }
  }

  close() {
    try {
      if (this.websocket) this.websocket.close();
    } catch { void 0 }
    this.websocket = null;
    this._connecting = false;
    this.printerName = null;
    this.pairedWith = null;
  }
}

const wsPrintClient = new WsPrintClient();
export default wsPrintClient;
export { WsPrintClient };